import { useEffect, useState } from "react";
import { Clapperboard, Loader2, CheckCircle2, Clock, XCircle } from "lucide-react";
import { toast } from "sonner";
import clientApi from "@/lib/clientApi";
import { formatApiError } from "@/lib/api";

const FIELD = "w-full bg-white border border-sand-200 rounded-lg px-3 py-2.5 text-sm text-charcoal-800 focus:outline-none focus:ring-2 focus:ring-sage/50";
const STATUS = {
  pending: { icon: Clock, cls: "bg-sand-200 text-charcoal-600", label: "In review" },
  approved: { icon: CheckCircle2, cls: "bg-sage/15 text-sage", label: "Live on the site!" },
  rejected: { icon: XCircle, cls: "bg-red-50 text-red-600", label: "Not published" },
};

export const PetStoryShare = ({ client }) => {
  const [stories, setStories] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    pet_name: client?.pets?.[0]?.name || "", caption: "", link: "", consent: false, file: null,
  });

  const load = () => clientApi.get("/client/pet-stories").then((r) => setStories(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.consent) return toast.error("Please confirm you're happy for us to share it.");
    if (!form.file && !form.link.trim()) return toast.error("Add a clip or paste a video link.");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("pet_name", form.pet_name);
      fd.append("caption", form.caption);
      fd.append("link", form.link.trim());
      fd.append("consent", "true");
      if (form.file) fd.append("file", form.file);
      await clientApi.post("/client/pet-stories", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Thank you! We'll review it and let you know when it's live. 🐾");
      setOpen(false);
      setForm({ pet_name: client?.pets?.[0]?.name || "", caption: "", link: "", consent: false, file: null });
      load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <section data-testid="portal-pet-story" id="pet-story">
      <h2 className="flex items-center gap-2 font-serif text-xl text-charcoal-800 mb-4">
        <Clapperboard size={18} className="text-terracotta" /> Share your pet's arrival story
      </h2>
      <div className="rounded-xl border border-sand-200 bg-white p-5">
        <p className="text-sm text-charcoal-600">
          Got a clip of {client?.pets?.[0]?.name || "your pet"} discovering Mérida? Share it — real arrivals are
          the best encouragement for nervous pet parents still deciding.
        </p>
        {!open ? (
          <button data-testid="pet-story-open" onClick={() => setOpen(true)}
            className="mt-4 rounded-full bg-terracotta px-5 py-2 text-sm font-medium text-white hover:bg-terracotta-hover">
            Share a clip
          </button>
        ) : (
          <div className="mt-4 space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <input data-testid="pet-story-petname" className={FIELD} placeholder="Pet's name"
                value={form.pet_name} onChange={(e) => setForm({ ...form, pet_name: e.target.value })} />
              <input data-testid="pet-story-link" className={FIELD} placeholder="Or paste a YouTube/Vimeo link"
                value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} />
            </div>
            <textarea data-testid="pet-story-caption" rows={2} className={FIELD}
              placeholder="One or two lines about the moment (optional)"
              value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })} />
            <div className="flex items-center gap-3">
              <label className="cursor-pointer rounded-full border border-sand-300 px-4 py-2 text-xs font-medium text-charcoal-700 hover:border-terracotta hover:text-terracotta">
                {form.file ? form.file.name : "Upload a short clip (MP4/MOV, max 30 MB)"}
                <input data-testid="pet-story-file" type="file" accept="video/mp4,video/quicktime,video/webm,video/x-m4v"
                  className="hidden" onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })} />
              </label>
            </div>
            <label className="flex items-start gap-2 text-xs text-charcoal-600">
              <input data-testid="pet-story-consent" type="checkbox" className="mt-0.5 accent-terracotta"
                checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} />
              I'm happy for Hola Movers to share this publicly on the website and social media.
            </label>
            <div className="flex gap-2">
              <button data-testid="pet-story-submit" onClick={submit} disabled={busy}
                className="inline-flex items-center gap-1.5 rounded-full bg-terracotta px-5 py-2 text-sm font-medium text-white hover:bg-terracotta-hover disabled:opacity-60">
                {busy && <Loader2 size={14} className="animate-spin" />} Submit for review
              </button>
              <button onClick={() => setOpen(false)} className="rounded-full border border-sand-300 px-5 py-2 text-sm text-charcoal-700 hover:bg-sand-50">Cancel</button>
            </div>
          </div>
        )}
        {stories.length > 0 && (
          <div className="mt-4 space-y-1.5" data-testid="pet-story-list">
            {stories.map((s) => {
              const st = STATUS[s.status] || STATUS.pending;
              return (
                <div key={s.id} className="flex items-center justify-between gap-3 rounded-lg bg-sand-100/70 px-3 py-2 text-sm">
                  <span className="truncate text-charcoal-700">{s.pet_name || "Pet"} — {s.caption || (s.kind === "file" ? "video clip" : "video link")}</span>
                  <span className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-medium ${st.cls}`}>
                    <st.icon size={11} /> {s.status === "approved" && s.video_slug
                      ? <a href={`/life-in-mexico/${s.video_slug}`} target="_blank" rel="noreferrer" className="hover:underline">{st.label}</a>
                      : st.label}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};
